import React, { useState, useCallback, useMemo, useContext } from "react";
import moment from "moment";
import { DocumentType } from "src/utils/enum";
import { formatSafeTableDate } from "../../../../../../utils/formatter";
import { openDocumentDetail } from "../../../../../../utils/documentModal";
import MatchItem from "./MatchItem";
import {
  getName,
  getDescription,
  getDocumentLabelWithNumber,
  getFolioDetalle,
  getMatchUserInfo,
} from "./matchRowHelpers";
import { BsLightningChargeFill } from "react-icons/bs";
import { RiRobot2Line } from "react-icons/ri";
import { HiSparkles } from "react-icons/hi";
import FeatherIcon from "feather-icons-react";
import { Icon } from "semantic-ui-react";
import aiIcon from "../../../../../../assets/icons/ai-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { GenerateMultipleUnMatch } from "../../../../../../graphql/conciliacion";
import { DeleteDocuments } from "../../../../../../graphql/taxDocument";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importOpenSidebar } from "../../../../../../redux/actions/openSidebar";
import { setUpdateDocuments } from "../../../../../../redux/actions/documentostributarios";
import { importUpdateTransactions } from "../../../../../../redux/actions/conciliacion";
import { updateConcilieantionRatio } from "../../../../../../redux/actions/dashboard";
import CustomModal from "../../../../../configuracion/components/customModal";
import CreateJournalEntryModal from "../contabilidad/CreateJournalEntryModal";
import RevisarMatchModal from "./RevisarMatchModal";
import ModalTransactionDetail from "./ModalTransactionDetail";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import { UserContext } from "src/context";
import { showAccounting } from "../../../../../../utils/accounting";
import useModulePermissions from "src/hooks/useModulePermissions";
import { mergeTransactionMatchsPreserveEntries } from "../../../../../../utils/mergeTransactionMatchs";
import { dt } from "../../../../../../utils/documentsI18n";
import DeleteReconciliationModalContent, {
  shouldDeleteObligationOnUnmatch,
  shouldWarnObligationRemovalOnUnmatch,
} from './DeleteReconciliationModalContent';
import { getUnmatchErrorAlert } from "../utils/unmatchInput";
import { isHardBlockedSummary, useStatementUnmatchGuard } from "../hooks/useStatementUnmatchGuard";
import StatementLinkedUnmatchModal from "./StatementLinkedUnmatchModal";
import "./MatchRow.scss";

/**
 * MatchRow - Complete match row component including item and footer
 * Handles all match data processing and rendering
 */
const MatchRow = ({
  match,
  index,
  matchContainer,
  editData,
  history,
  location,
  isNewMatch = false,
  readOnly = false,
  transactionAmount,
}) => {
  const dispatch = useDispatch();
  const { roles } = useContext(UserContext);
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { options } = openSidebar;
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const currentUser = useSelector((state) => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showJournalEntryModal, setShowJournalEntryModal] = useState(false);
  const [showRevisarMatchModal, setShowRevisarMatchModal] = useState(false);
  const [showTransactionDetailModal, setShowTransactionDetailModal] = useState(false);
  const { executeMutation: generateMultipleUnMatch } = GenerateMultipleUnMatch();
  const {
    summary: statementUnmatchSummary,
    setSummary: setStatementUnmatchSummary,
    fetchSummaryForPair,
  } = useStatementUnmatchGuard(companyId);
  const [bankUnmatchWarning, setBankUnmatchWarning] = useState([]);

  // Fila embebida del match: en transacción → MatchTransaction hacia documento; en documento/obligación → inMatch hacia transacción (monto parcial, matchSource, userRelated).
  const transactionMatchRecord = useMemo(() => {
    if (!match) return null;

    if (Array.isArray(editData?.matchs) && editData?.documentType === undefined) {
      const stampedMatchId = match.matchId != null ? String(match.matchId) : '';
      const inMatchId = match?.meta?.matchs?.inMatch?.[0]?.matchId;
      const resolvedMatchId = stampedMatchId || (inMatchId != null ? String(inMatchId) : '');
      if (resolvedMatchId) {
        return (editData.matchs || []).find((m) => String(m?.matchId) === resolvedMatchId) || null;
      }
      const docId = match.id ?? match.docId ?? match._id;
      if (docId == null) return null;
      const idStr = String(docId);
      return (editData.matchs || []).find((m) => String(m?.docId) === idStr) || null;
    }

    if (editData?.documentType !== undefined) {
      const inMatch = editData?.meta?.matchs?.inMatch || [];
      const outMatch = editData?.meta?.matchs?.outMatch || [];
      const documentMatchs = [...inMatch, ...outMatch];
      const stampedMatchId = match.matchId != null ? String(match.matchId) : '';
      if (stampedMatchId) {
        return documentMatchs.find((m) => String(m?.matchId) === stampedMatchId) || null;
      }
      const txId = String(match.id ?? match._id ?? match.docId ?? '');
      if (!txId) return null;
      return (
        documentMatchs.find(
          (m) => String(m?.id) === txId || String(m?.docId) === txId,
        ) || null
      );
    }

    return null;
  }, [
    editData?.matchs,
    editData?.documentType,
    editData?.meta?.matchs?.inMatch,
    editData?.meta?.matchs?.outMatch,
    match?.id,
    match?.docId,
    match?._id,
    match?.matchId,
    match?.meta?.matchs?.inMatch,
  ]);

  const effectiveMatch = useMemo(
    () => ({ ...match, ...(transactionMatchRecord || {}) }),
    [match, transactionMatchRecord],
  );

  const openRevisarMatchModal = useCallback(() => setShowRevisarMatchModal(true), []);

  const { deleteDocuments } = DeleteDocuments();

  const isDocument = options?.type === "documents";

  // Use helper functions to extract match data
  const name = getName(match);
  const documentLabelWithNumber = getDocumentLabelWithNumber(match);
  const folioDetalle = getFolioDetalle(match);

  // Dynamic title for Categorizar Match modal: [Abono|Cargo] por [monto] por [contraparte] (más legible)
  const revisarMatchModalTitle = useMemo(() => {
    const isEditDataTransaction = editData?.documentType === undefined;
    const tx = isEditDataTransaction
      ? editData
      : (['RECIEVED', 'RECEIVED', 'SPEND', 'GIVEN'].includes(match?.type) ? match : null);
    if (!tx) return 'Categorizar Match';
    const counterpart = isEditDataTransaction ? match : editData;
    const counterpartName =
      counterpart === match
        ? (name || documentLabelWithNumber || '—')
        : (getName(counterpart) || getDocumentLabelWithNumber(counterpart) || '—');
    const typeLabel =
      tx.type === 'RECIEVED' || tx.type === 'RECEIVED' ? 'Abono' : 'Cargo';
    const titleAmount =
      transactionMatchRecord?.localValue ??
      transactionMatchRecord?.localAmount ??
      transactionMatchRecord?.matchedAmount ??
      transactionMatchRecord?.amount ??
      tx.amountCLP ??
      tx.amount;
    const amountFormatted = formatMoneyWithCurrency(
      titleAmount,
      'CLP',
      true,
      true
    );
    const counterpartClean = (counterpartName || '')
      .replace(/^\d+\s+/, '')
      .trim() || counterpartName || '—';
    return `${typeLabel} por ${amountFormatted} por ${counterpartClean}`;
  }, [editData, match, name, documentLabelWithNumber, transactionMatchRecord]);

  // Get match user info including matchSource (usar fila inMatch/MatchTransaction cuando el contenedor es documento)
  const { matchedByEmail, matchCreationDate, matchSource, displayMatchSource } = getMatchUserInfo(
    effectiveMatch,
    editData,
  );
  const formattedMatchCreationDate =
    formatSafeTableDate(matchCreationDate, "DD/MM/YYYY HH:mm") ?? "";

  const isUniqueStrongCandidate = displayMatchSource === 'automatic_strong_candidate';
  const isExactCandidates =
    matchSource === 'automatic_exact_candidates';
  const isIvaPostergado = matchSource === 'automatic_iva_postergado';
  const isOutsourceMatch = matchSource === 'automatic_outsource';

  // confirmed_strong_candidate by a real user displays as manual with user info
  const isConfirmedStrongCandidate =
    matchSource === 'confirmed_strong_candidate' && displayMatchSource === 'manual';

  // Handler to open document detail modal
  const handleDocumentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (match.id && [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType)) {
      openDocumentDetail(match.id, history, location);
    }
  };

  // Handler to open transaction detail modal
  const handleTransactionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const transactionId = match.id || match.docId;
    if (transactionId && ["SPEND", "GIVEN", "RECIEVED", "RECEIVED"].includes(match.type)) {
      setShowTransactionDetailModal(true);
    }
  };

  const handleGoToMovement = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const baseId = match.id || match.docId || match._id;
    const isTransaction = ["SPEND", "GIVEN", "RECIEVED", "RECEIVED"].includes(match.type);
    const isDocument = [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType);

    if (baseId && isTransaction) {
      const params = new URLSearchParams({
        type: "ALL",
        page: "1",
        offset: "50",
        transactionId: baseId,
        isUnique: "true",
      });
      window.open(`/conciliacion?${params.toString()}`, "_blank");
      return;
    }

    if (baseId && isDocument) {
      const params = new URLSearchParams({
        type: "ALL",
        page: "1",
        offset: "50",
        documentId: baseId,
        id: baseId,
        isUnique: "true",
      });
      window.open(`/documentos_tributarios?${params.toString()}`, "_blank");
    }
  };

  // Get match amount and currency
  // IMPORTANT: For partial matches (when document amount > transaction amount),
  // matchedAmount contains the partial amount reconciled from the document.
  // We should prioritize matchedAmount when available to show the correct partial amount.
  const matchKey = match.matchId || match.id || match.docId || match.settleId;

  const matchAmount =
    transactionMatchRecord?.amount ??
    transactionMatchRecord?.matchedAmount ??
    match.localValue ??
    match.localAmount ??
    match.matchedAmount ??
    match.amount ??
    0;
  
  // FX línea "Se han pagado … a una tasa …": vive en el match embebido de la transacc TRANSACTION→doc.
  // Priorizar transactionMatchRecord (docId = obligación) por encima del documento enriquecido.
  let originalAmount =
    transactionMatchRecord?.originalAmount ?? match.originalAmount;
  let exchangeRate =
    transactionMatchRecord?.exchangeRate ?? match.exchangeRate;

  if (
    originalAmount == null ||
    originalAmount === undefined ||
    exchangeRate == null ||
    exchangeRate === undefined
  ) {
    const editDataIsDocument = editData?.documentType !== undefined;
    const matchs = editDataIsDocument
      ? editData?.meta?.matchs?.inMatch || []
      : editData?.matchs || [];
    const keyStr = matchKey != null ? String(matchKey) : '';
    const foundMatch = matchs.find(
      (m) =>
        String(m?.matchId || '') === keyStr ||
        String(m?.id) === keyStr ||
        String(m?.docId) === keyStr ||
        String(m?.settleId) === keyStr
    );
    if (foundMatch) {
      if (originalAmount == null || originalAmount === undefined) {
        originalAmount = foundMatch.originalAmount;
      }
      if (exchangeRate == null || exchangeRate === undefined) {
        exchangeRate = foundMatch.exchangeRate;
      }
    }
  }

  // Último recurso: localValue (CLP) y matchedAmount (moneda del movimiento) guardados por el backend
  const txRec = transactionMatchRecord;
  if (
    (exchangeRate == null ||
      exchangeRate === undefined ||
      Number(exchangeRate) <= 1 ||
      originalAmount == null ||
      originalAmount === undefined) &&
    txRec &&
    txRec.localValue != null &&
    txRec.matchedAmount != null
  ) {
    const mo = Number(txRec.matchedAmount);
    const lv = Number(txRec.localValue);
    if (mo > 0 && lv > 0) {
      if (originalAmount == null || originalAmount === undefined) {
        originalAmount = mo;
      }
      if (exchangeRate == null || exchangeRate === undefined || Number(exchangeRate) <= 1) {
        exchangeRate = lv / mo;
      }
    }
  }

  // Obligación con meta.exchange (p. ej. regla o match viejo sin FX en transaction.matchs): completar FX para la ficha.
  const exMeta =
    match?.documentType === DocumentType.OBLIGATION ? match?.meta?.exchange : null;
  if (exMeta?.originalCurrency && exMeta.originalCurrency !== 'CLP') {
    const foreign = Number(exMeta.originalAmount);
    const local = Number(exMeta.localAmount);
    const oa = Number(originalAmount);
    if (originalAmount == null || originalAmount === undefined) {
      originalAmount = exMeta.originalAmount;
    } else if (
      local > 0 &&
      foreign > 0 &&
      Number.isFinite(oa) &&
      Math.abs(oa - local) < 0.01
    ) {
      originalAmount = exMeta.originalAmount;
    }
    if (
      exchangeRate == null ||
      exchangeRate === undefined ||
      !Number.isFinite(Number(exchangeRate)) ||
      Number(exchangeRate) <= 1
    ) {
      exchangeRate = exMeta.exchangeRate;
    }
  }

  const editDataIsDocument = editData?.documentType !== undefined;
  let originalCurrencyForMatch =
    transactionMatchRecord?.currency ||
    (!editDataIsDocument
      ? editData?.currency || editData?.currencyType
      : match.currency);

  if (
    !editDataIsDocument &&
    match?.documentType === DocumentType.OBLIGATION &&
    exMeta?.originalCurrency &&
    exMeta.originalCurrency !== 'CLP' &&
    (originalCurrencyForMatch === 'CLP' || !originalCurrencyForMatch)
  ) {
    originalCurrencyForMatch = exMeta.originalCurrency;
  }

  // Regla única: el monto principal del match SIEMPRE se muestra en CLP.
  // Backend siempre escribe localValue en CLP (también para CLP→CLP donde localValue === amount),
  // así que se prioriza localValue. amount/matchedAmount son fallback para registros legacy.
  const displayAmount =
    transactionMatchRecord?.localValue ??
    transactionMatchRecord?.localAmount ??
    match?.localValue ??
    match?.localAmount ??
    transactionMatchRecord?.matchedAmount ??
    transactionMatchRecord?.amount ??
    match?.matchedAmount ??
    matchContainer?.[matchKey + "matchedAmount"] ??
    matchContainer?.[matchKey] ??
    matchAmount;
  const displayCurrency = 'CLP';

  const amount = displayAmount;
  const currency = displayCurrency;

  const matchDate = moment(match.documentDate || match.date);

  const { canCreateAsiento } = useModulePermissions();
  const canUseAccounting = showAccounting(roles, selectedCompany) && canCreateAsiento();

  const normalizeId = (value) => {
    if (!value) return value;
    const str = String(value);
    // Some lists suffix ids with "-0" to disambiguate rows; backend expects raw id.
    return str.endsWith('-0') ? str.replace(/-0$/, '') : str;
  };

  const handleRequestDelete = async () => {
    const originId = normalizeId(
      isDocument ? (editData?._id || editData?.id) : (editData?.id || editData?._id)
    );
    const endId = normalizeId(match?.id || match?.docId || match?._id);
    const preflight = await fetchSummaryForPair(originId, endId);
    if (isHardBlockedSummary(preflight)) {
      setStatementUnmatchSummary(preflight);
      return;
    }
    setBankUnmatchWarning(preflight?.blocked === false ? preflight.bankTransactions || [] : []);
    setShowDeleteModal(true);
  };

  const getUnmatchEndType = () => {
    // IMPORTANT: Check for docId first - if it exists, this is a transaction match (MatchTransaction)
    // In MatchTransaction: docId points to the document, type indicates document type (OBLIGATION/TAX_DOCUMENT)
    // But the match itself IS a transaction, so we should return 'TRANSACTION'
    if (match?.docId) {
      return 'TRANSACTION';
    }
    
    // For actual document matches, backend expects documentType (e.g. OBLIGATION/TAX_DOCUMENT)
    if (match?.documentType) return match.documentType;
    
    // For pure transactions, normalize SPEND/RECEIVED/GIVEN to TRANSACTION
    // Backend expects "TRANSACTION" not the specific transaction type
    const transactionType = match?.type || 'TRANSACTION';
    if (transactionType === 'SPEND' || transactionType === 'RECEIVED' || transactionType === 'RECIEVED' || transactionType === 'GIVEN') {
      return 'TRANSACTION';
    }
    
    return transactionType;
  };

  // Prepare initial data for journal entry modal
  // This combines match data with editData to provide context
  const getJournalEntryInitialData = () => {
    // Determine originType from match documentType
    const originType = match.documentType || 'TAX_DOCUMENT';
    
    // For OBLIGATION only use document glosa (meta.description) as default description
    const documentGlosa = match?.documentType === DocumentType.OBLIGATION ? getDescription(match) : '';
    const description = (documentGlosa && documentGlosa.trim()) ? documentGlosa.trim() : (name || documentLabelWithNumber || '');
    
    // Extract matchId from match object or from editData matchs
    let matchId = match.matchId || null;
    if (!matchId) {
      // Try to find matchId from editData matchs (use String() for reliable comparison)
      const isDoc = editData?.documentType !== undefined;
      const matchs = isDoc ? editData?.meta?.matchs?.inMatch || [] : editData?.matchs || [];
      const matchKeyStr = matchKey != null ? String(matchKey) : '';
      const foundMatch = matchs.find(m =>
        String(m.matchId || '') === matchKeyStr ||
        String(m.id || '') === matchKeyStr ||
        String(m.docId || '') === matchKeyStr ||
        String(m.settleId || '') === matchKeyStr
      );
      if (foundMatch?.matchId) {
        matchId = foundMatch.matchId;
      }
    }
    // Fallback: when match is a document (e.g. obligation), get matchId from its outMatch
    if (!matchId && match?.meta?.matchs?.outMatch && Array.isArray(match.meta.matchs.outMatch)) {
      const editDataId = editData?.id ?? editData?._id;
      if (editDataId != null) {
        const editIdStr = String(editDataId);
        const outMatchEntry = match.meta.matchs.outMatch.find(
          m => String(m.id || '') === editIdStr || String(m.docId || '') === editIdStr
        );
        if (outMatchEntry?.matchId) {
          matchId = outMatchEntry.matchId;
        }
      }
    }
    
    // Determine source types explicitly
    // editData could be either a transaction or document, determine from context
    const editDataSourceType = editData?.documentType !== undefined ? 'DOCUMENT' : 'TRANSACTION';
    const matchSourceType = match?.documentType !== undefined ? 'DOCUMENT' : 'TRANSACTION';
    
    // Build explicit sources array
    const sources = [];
    if (editData) {
      sources.push({
        type: editDataSourceType,
        id: editData.id || editData._id
      });
    }
    if (match) {
      sources.push({
        type: matchSourceType,
        id: match.id || match.docId || match._id
      });
    }
    
    // Build initialData structure
    // Only put editData in transactions array if it's actually a transaction
    // Otherwise put it in documents array
    const isEditDataTransaction = editData && editData.documentType === undefined;
    const isEditDataDocument = editData && editData.documentType !== undefined;
    
    return {
      originType: originType,
      localAmount: amount || 0,
      description: description,
      matchId: matchId,
      // Explicit sources array with type and id
      sources: sources,
      support: {
        transactions: isEditDataTransaction ? [editData] : [],
        documents: [
          ...(isEditDataDocument && editData ? [editData] : []),
          ...(match ? [match] : []),
        ],
      },
      // Keep origin for conditional rendering
      origin: 'MATCH',
    };
  };

  // Handler to delete/unmatch
  const handleDeleteMatch = async () => {
    setIsDeleting(true);

    try {
      // Get origin ID - handle both _id and id formats
      const originId = normalizeId(
        isDocument
          ? (editData?._id || editData?.id)
          : (editData?.id || editData?._id)
      );
      
      // Get end ID - handle both id and docId formats
      const endId = normalizeId(match?.id || match?.docId || match?._id);
      
      // Validate that we have both IDs
      if (!originId || !endId) {
        dispatch(
          importAlertState({
            content: dt('reconcile.unmatchIdentifyError'),
            state: "error",
            duration: 4,
          })
        );
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      // Build the input for GenerateMultipleUnMatch
      // Check if match has settleId or type === 'MANUAL' to determine if it's a manual match
      const isManualMatch = match.settleId || match.type === 'MANUAL';
      
      // Get matchId if available (for partial matches deletion)
      // Try multiple sources: direct matchId, from match object, or from editData matchs
      let matchId = match.matchId || match.match_id || null;
      if (!matchId) {
        // Try to find matchId from editData matchs
        const isDocument = editData?.documentType !== undefined;
        const matchs = isDocument ? editData?.meta?.matchs?.inMatch || [] : editData?.matchs || [];
        const matchKey = match.matchId || match.id || match.docId || match.settleId;
        const foundMatch = matchs.find(m =>
          String(m.matchId || '') === String(matchKey) ||
          String(m.id) === String(matchKey) || 
          String(m.docId) === String(matchKey) || 
          String(m.settleId) === String(matchKey)
        );
        if (foundMatch?.matchId) {
          matchId = foundMatch.matchId;
        }
      }
      
      const input = [{
        originType: isDocument ? (editData.documentType || "TAX_DOCUMENT") : "TRANSACTION",
        endType: getUnmatchEndType(),
        originId: originId,
        endId: endId,
        companyId,
        ...(matchId && { matchId: matchId }), // Include matchId if available for partial match deletion
      }];

      const { data, error } = await generateMultipleUnMatch({ input });

      if (error) {
        dispatch(
          importAlertState({
            ...getUnmatchErrorAlert(error, dt('reconcile.unmatchError')),
          })
        );
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      // Update Redux state with fresh data
      if (data?.generateMultipleUnMatch) {
        let hasErrors = false;
        let hasSuccess = false;
        for (const response of data.generateMultipleUnMatch) {
          // Verificar si data es null o no tiene la estructura esperada
          if (!response.data || !response.data["__typename"]) {
            hasErrors = true;
            continue;
          }

          hasSuccess = true;
          const type = response.data["__typename"];

          if (type === "Transaction") {
            const { remaining, matchs } = response.data;
            const newEditData = {
              ...editData,
              ...{
                remaining,
                matchs: mergeTransactionMatchsPreserveEntries(editData.matchs, matchs),
              },
            };
            dispatch(updateConcilieantionRatio());
            dispatch(
              importOpenSidebar({
                show: false,
                reconcile: true,
                editData: newEditData,
                options,
              })
            );
          } else if (type === "Document") {
            const newMeta = response.data.meta;
            const newEditData = {
              ...editData,
              meta: {
                ...editData.meta,
                ...{
                  remaining: newMeta.remaining,
                  isPaid: newMeta.isPaid,
                  matchs: newMeta.matchs,
                },
              },
            };

            dispatch(
              importOpenSidebar({
                show: false,
                reconcile: true,
                editData: newEditData,
                options,
              })
            );
          }
        }
        
        if (hasErrors) {
          dispatch(
            importAlertState({
              content: dt('reconcile.unmatchError'),
              state: "error",
              duration: 4,
            })
          );
        }
        
        // Actualizar documentos y transacciones si hubo al menos una respuesta exitosa
        if (hasSuccess) {
          dispatch(setUpdateDocuments({ value: true }));
          dispatch(importUpdateTransactions(true));
        }
      }

      // Ocultar la obligación creada para el match (creationFor match) o por regla.
      // Retenciones BH y tarjetas de crédito nunca se eliminan al deshacer.
      const shouldDeleteObligation = shouldDeleteObligationOnUnmatch(effectiveMatch);
      if (shouldDeleteObligation) {
        const obligationId = normalizeId(effectiveMatch.id || effectiveMatch.docId);
        if (obligationId) {
          try {
            const deleteResult = await deleteDocuments({ 
              ids: [obligationId], 
              companyId 
            });
            
            if (deleteResult?.error) {
              const motivo = deleteResult.error.graphQLErrors?.[0]?.message;
              dispatch(
                importAlertState({
                  content: motivo
                    ? `${dt('reconcile.unmatchObligationWarning')} ${motivo}`
                    : dt('reconcile.unmatchObligationWarning'),
                  state: "warning",
                  duration: 4,
                })
              );
            }
          } catch (deleteError) {
            console.error("Error al eliminar la obligación:", deleteError);
            dispatch(
              importAlertState({
                content: "La conciliación se deshizo, pero hubo un problema al eliminar la obligación.",
                state: "warning",
                duration: 4,
              })
            );
          }
        }
      }
    } catch (error) {
      console.error("Error al ejecutar generateMultipleUnMatch:", error);
      dispatch(
        importAlertState({
          ...getUnmatchErrorAlert(error, dt('reconcile.unmatchError')),
        })
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <div 
        key={`match-${index}`} 
        className={`match-row-container ${isNewMatch ? 'match-row-container--fade-in' : ''}`}
      >
        <MatchItem
          match={match}
          matchForAccounting={effectiveMatch}
          documentLabelWithNumber={documentLabelWithNumber}
          folioDetalle={folioDetalle}
          matchDate={matchDate}
          amount={amount}
          currency={currency}
          name={name}
          onDocumentClick={handleDocumentClick}
          onTransactionClick={handleTransactionClick}
          onGoToMovementClick={handleGoToMovement}
          onDeleteClick={readOnly ? undefined : handleRequestDelete}
          onContabilizarClick={canUseAccounting && !readOnly ? () => setShowJournalEntryModal(true) : undefined}
          onRevisarMatchClick={!readOnly ? openRevisarMatchModal : undefined}
          originalAmount={originalAmount}
          exchangeRate={exchangeRate}
          originalCurrency={originalCurrencyForMatch}
          isManualMatch={match.type === 'MANUAL' && match.origin === 'manual'}
          isClayMatch={match.type === 'MANUAL' && match.origin === 'clay'}
          transactionType={!editDataIsDocument ? editData?.type : undefined}
        />
        <div className="match-row-footer">
          {/* Show different messages based on matchSource - NEVER show user for automatic matches */}
          {/* confirmed_strong_candidate by bot → same as automatic_strong_candidate; by user → manual footer */}
          {/* Un solo nodo de texto: si el padre es flex con gap, los Fragment <> dejan cada texto/hijo como ítem y separa la frase */}
          {displayMatchSource === 'automatic_rule' ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.autoReconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>
            </span>
          ) : isUniqueStrongCandidate || isExactCandidates ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.autoReconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>
            </span>
          ) : isIvaPostergado ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.autoReconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>
            </span>
          ) : isOutsourceMatch ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.autoReconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>
            </span>
          ) : displayMatchSource === 'automatic_heuristic' || displayMatchSource === 'automatic_ai' || displayMatchSource === 'automatic_credit' ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.autoReconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>
            </span>
          ) : (displayMatchSource === 'manual' || isConfirmedStrongCandidate) && matchedByEmail ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.reconciledOn')}{' '}
              <span className="match-row-footer-text">
                {matchCreationDate ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm') : ''}
              </span>{' '}
              {dt('reconcile.footer.by')}{' '}
              <span className="match-row-footer-text">{matchedByEmail}</span>
            </span>
          ) : (displayMatchSource === 'manual' || isConfirmedStrongCandidate) && formattedMatchCreationDate ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.reconciledOn')}{' '}
              <span className="match-row-footer-text">
                {moment(matchCreationDate).format('DD/MM/YYYY HH:mm')}
              </span>
            </span>
          ) : formattedMatchCreationDate ? (
            <span className="match-row-footer-copy">
              {dt('reconcile.footer.reconciledOn')}{' '}
              <span className="match-row-footer-text">
                {moment(matchCreationDate).format('DD/MM/YYYY HH:mm')}
              </span>
            </span>
          ) : null}
          {displayMatchSource && (
            <span className="match-row-footer-badge" style={{
              marginLeft: '4px',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              background: displayMatchSource === 'manual' 
                ? '#488E9D' 
                : displayMatchSource === 'automatic_rule'
                  ? '#14b8a6'
                  : isUniqueStrongCandidate
                    ? '#16a34a'
                  : isExactCandidates
                    ? 'rgb(26, 115, 232)'
                  : isOutsourceMatch
                    ? '#0f766e'
                  : (displayMatchSource === 'automatic_heuristic' || displayMatchSource === 'automatic_ai' || displayMatchSource === 'automatic_credit')
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#fce7f3',
              color: displayMatchSource === 'manual' 
                ? '#ffffff' 
                : displayMatchSource === 'automatic_rule'
                  ? '#ffffff'
                  : isUniqueStrongCandidate
                    ? '#ffffff'
                  : isExactCandidates
                    ? '#ffffff'
                  : isOutsourceMatch
                    ? '#ffffff'
                  : (displayMatchSource === 'automatic_heuristic' || displayMatchSource === 'automatic_ai' || displayMatchSource === 'automatic_credit')
                    ? '#ffffff' 
                    : '#9f1239',
              border: displayMatchSource === 'automatic_rule'
                ? '1px solid #14b8a6'
                : isUniqueStrongCandidate
                  ? '1px solid #16a34a'
                : isExactCandidates
                  ? '1px solid rgb(26, 115, 232)'
                : isOutsourceMatch
                  ? '1px solid #0f766e'
                : (displayMatchSource === 'automatic_heuristic' || displayMatchSource === 'automatic_ai' || displayMatchSource === 'automatic_credit')
                  ? 'none'
                  : `1px solid ${displayMatchSource === 'manual' 
                    ? '#488E9D' 
                    : '#fbcfe8'}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              {displayMatchSource === 'manual' 
                ? (
                  <FeatherIcon 
                    icon="user" 
                    size={11} 
                    strokeWidth={3}
                    style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block', color: 'white' }} 
                  />
                )
                : displayMatchSource === 'automatic_rule'
                  ? (
                    <>
                      <BsLightningChargeFill size={11} style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block' }} />
                      <span style={{ marginLeft: '4px' }}>{dt('reconcile.badges.rule')}</span>
                    </>
                  )
                  : isUniqueStrongCandidate
                    ? (
                      <>
                        <FeatherIcon icon="check-circle" size={11} strokeWidth={3} style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block', color: 'white' }} />
                        <span style={{ marginLeft: '4px' }}>{dt('reconcile.badges.uniqueStrong')}</span>
                      </>
                    )
                  : isExactCandidates
                    ? (
                      <>
                        <FeatherIcon icon="check-circle" size={11} strokeWidth={3} style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block', color: 'white' }} />
                        <span style={{ marginLeft: '4px' }}>{dt('reconcile.badges.exactCandidates')}</span>
                      </>
                    )
                  : isOutsourceMatch
                    ? (
                      <>
                        <FeatherIcon icon="external-link" size={11} strokeWidth={3} style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block', color: 'white' }} />
                        <span style={{ marginLeft: '4px' }}>{dt('reconcile.badges.outsource')}</span>
                      </>
                    )
                  : (displayMatchSource === 'automatic_heuristic' || displayMatchSource === 'automatic_ai' || displayMatchSource === 'automatic_credit')
                    ? (
                      <>
                        {dt('reconcile.badges.ai')}{' '}
                        <img 
                          src={aiIcon} 
                          alt="IA" 
                          style={{ 
                            width: '14px', 
                            height: '14px', 
                            margin: '0 3px',
                            verticalAlign: 'middle', 
                            display: 'inline-block',
                            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                          }} 
                        />
                      </>
                    )
                    : (
                      <FeatherIcon 
                        icon="user" 
                        size={11} 
                        strokeWidth={3}
                        style={{ margin: 0, verticalAlign: 'middle', display: 'inline-block', color: 'white' }} 
                      />
                    )}
            </span>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
          }
        }}
        title={dt('reconcile.deleteMatchTitle')}
        size="small"
        dimmer="blurring"
        closeOnDimmerClick={!isDeleting}
        closeOnEscape={!isDeleting}
        classNameModal="delete-obligation-modal"
        classNameContent="delete-obligation-modal-content"
        content={
          <DeleteReconciliationModalContent
            documentLabel={documentLabelWithNumber}
            counterpartName={name}
            amount={amount}
            currency={currency}
            matchDate={match.documentDate || match.date}
            willRemoveObligation={shouldWarnObligationRemovalOnUnmatch(match)}
            bankTransactions={bankUnmatchWarning}
          />
        }
        onConfirm={handleDeleteMatch}
        confirmLabel={dt('deleteObligation.confirmDelete')}
        confirmDisabled={isDeleting}
        isLoading={isDeleting}
        confirmVariant="danger"
        cancelarLabel={dt('common.cancel')}
      />

      <StatementLinkedUnmatchModal
        open={Boolean(statementUnmatchSummary)}
        summary={statementUnmatchSummary}
        onClose={() => setStatementUnmatchSummary(null)}
      />

      <RevisarMatchModal
        open={showRevisarMatchModal}
        onClose={() => setShowRevisarMatchModal(false)}
        editData={editData}
        match={transactionMatchRecord ?? match}
        companyId={companyId}
        title={revisarMatchModalTitle}
        onSuccess={() => setShowRevisarMatchModal(false)}
      />

      {/* Journal Entry Modal */}
      {canUseAccounting && (
        <CreateJournalEntryModal
          open={showJournalEntryModal}
          onClose={() => setShowJournalEntryModal(false)}
          editData={getJournalEntryInitialData()}
        />
      )}

      {/* Transaction Detail Modal */}
      <ModalTransactionDetail
        isVisible={showTransactionDetailModal}
        onClose={() => setShowTransactionDetailModal(false)}
        transaction={match}
      />
    </>
  );
};

export default MatchRow;

